<template>
  <div class="scheduled-tasks">
    <div class="header">
      <h2>定时任务管理</h2>
      <el-button type="primary" @click="showCreateDialog" icon="el-icon-plus">
        新建任务
      </el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="filter-bar">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索任务名称或API接口"
        clearable
        class="search-input"
        @clear="handleSearch"
        @keyup.enter.native="handleSearch"
      >
        <i slot="prefix" class="el-icon-search input-icon"></i>
      </el-input>
      <el-button type="primary" @click="handleSearch">
        <i class="el-icon-search"></i>
        <span>搜索</span>
      </el-button>
      <el-button icon="el-icon-refresh" @click="loadTasks" :loading="loading">
        刷新
      </el-button>
    </div>

    <!-- 任务列表 -->
    <div class="tasks-table">
      <el-table
        :data="filteredTasks"
        v-loading="loading"
        style="width: 100%"
        :header-cell-style="{ background: 'rgba(30, 41, 59, 0.8)', color: '#e2e8f0' }"
        :cell-style="{ background: 'rgba(15, 23, 42, 0.6)', color: '#cbd5e1' }"
      >
        <el-table-column prop="task_name" label="任务名称" width="280" />
        <el-table-column prop="api_endpoint" label="API接口" min-width="200" />
        <el-table-column prop="schedule_type" label="调度类型" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.schedule_type === 'cron' ? 'primary' : 'success'" size="small">
              {{ scope.row.schedule_type === 'cron' ? 'Cron表达式' : '间隔执行' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="调度配置" width="180">
          <template slot-scope="scope">
            {{ formatScheduleConfig(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column label="下次执行" width="160">
          <template slot-scope="scope">
            {{ scope.row.next_run_time || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.enabled"
              @change="toggleTask(scope.row)"
              :active-value="true"
              :inactive-value="false"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="executeNow(scope.row)">
              立即执行
            </el-button>
            <el-button size="mini" type="text" @click="showHistory(scope.row)">
              执行历史
            </el-button>
            <el-button size="mini" type="text" @click="editTask(scope.row)">
              编辑
            </el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="deleteTask(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      :title="dialogMode === 'create' ? '新建定时任务' : '编辑定时任务'"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
      :append-to-body="true"
      custom-class="task-dialog"
    >
      <el-form :model="taskForm" :rules="taskRules" ref="taskForm" label-width="100px">
        <el-form-item label="任务名称" prop="task_name">
          <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" />
        </el-form-item>

        <el-form-item label="API接口" prop="api_endpoint">
          <el-input v-model="taskForm.api_endpoint" placeholder="/stock/bill/history/fetch" />
          <div class="form-tip">后端API接口路径，如: /stock/bill/history/fetch</div>
        </el-form-item>

        <el-form-item label="HTTP方法" prop="http_method">
          <el-select v-model="taskForm.http_method" style="width: 100%">
            <el-option label="POST" value="POST" />
            <el-option label="GET" value="GET" />
          </el-select>
        </el-form-item>

        <el-form-item label="请求参数">
          <el-input
            type="textarea"
            :rows="3"
            v-model="parametersJson"
            placeholder='{"stock_code": "000001"}'
          />
          <div class="form-tip">JSON格式的请求参数</div>
        </el-form-item>

        <el-form-item label="调度类型" prop="schedule_type">
          <el-radio-group v-model="taskForm.schedule_type" @change="onScheduleTypeChange">
            <el-radio label="cron">Cron表达式</el-radio>
            <el-radio label="interval">间隔执行</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- Cron配置 -->
        <template v-if="taskForm.schedule_type === 'cron'">
          <el-form-item label="执行时间">
            <div class="cron-config">
              <el-input-number
                v-model="taskForm.schedule_config.hour"
                :min="0"
                :max="23"
                placeholder="时"
                controls-position="right"
                style="width: 100px"
              />
              <span>:</span>
              <el-input-number
                v-model="taskForm.schedule_config.minute"
                :min="0"
                :max="59"
                placeholder="分"
                controls-position="right"
                style="width: 100px"
              />
            </div>
            <div class="form-tip">每天在指定时间执行（24小时制）</div>
          </el-form-item>
        </template>

        <!-- 间隔配置 -->
        <template v-if="taskForm.schedule_type === 'interval'">
          <el-form-item label="执行间隔">
            <div class="interval-config">
              <el-input-number
                v-model="intervalValue"
                :min="1"
                placeholder="间隔值"
                controls-position="right"
                style="width: 150px"
              />
              <el-select v-model="intervalUnit" style="width: 120px">
                <el-option label="秒" value="seconds" />
                <el-option label="分钟" value="minutes" />
                <el-option label="小时" value="hours" />
                <el-option label="天" value="days" />
              </el-select>
            </div>
          </el-form-item>
        </template>

        <el-form-item label="启用状态">
          <el-switch v-model="taskForm.enabled" />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveTask" :loading="saving">确定</el-button>
      </div>
    </el-dialog>

    <!-- 执行历史对话框 -->
    <el-dialog
      title="执行历史"
      :visible.sync="historyDialogVisible"
      width="800px"
      :append-to-body="true"
      custom-class="history-dialog"
    >
      <el-table
        :data="executions"
        v-loading="loadingHistory"
        style="width: 100%"
        :header-cell-style="{ background: 'rgba(30, 41, 59, 0.8)', color: '#e2e8f0' }"
        :cell-style="{ background: 'rgba(15, 23, 42, 0.6)', color: '#cbd5e1' }"
        max-height="400"
      >
        <el-table-column prop="execution_time" label="执行时间" width="160" />
        <el-table-column label="状态" width="80">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.status === 'success' ? 'success' : scope.row.status === 'failed' ? 'danger' : 'info'"
              size="small"
            >
              {{ statusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="结果/错误" min-width="200">
          <template slot-scope="scope">
            <div class="result-cell" v-if="scope.row.status === 'success'">
              {{ scope.row.response_data ? '执行成功' : '-' }}
            </div>
            <div class="result-cell error" v-else-if="scope.row.status === 'failed'">
              {{ scope.row.error_message ? scope.row.error_message.substring(0, 100) : '执行失败' }}
            </div>
            <div class="result-cell" v-else>运行中...</div>
          </template>
        </el-table-column>
      </el-table>

      <div slot="footer" class="dialog-footer">
        <el-button @click="historyDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ScheduledTasksView',
  data() {
    return {
      tasks: [],
      loading: false,
      dialogVisible: false,
      historyDialogVisible: false,
      dialogMode: 'create',
      saving: false,
      loadingHistory: false,
      executions: [],
      currentTask: null,
      intervalValue: 10,
      intervalUnit: 'seconds',
      parametersJson: '',
      searchKeyword: '',
      taskForm: {
        task_name: '',
        api_endpoint: '',
        http_method: 'POST',
        parameters: {},
        schedule_type: 'cron',
        schedule_config: {
          hour: 16,
          minute: 0,
          second: 0
        },
        enabled: true
      },
      taskRules: {
        task_name: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        api_endpoint: [
          { required: true, message: '请输入API接口', trigger: 'blur' }
        ],
        schedule_type: [
          { required: true, message: '请选择调度类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    filteredTasks() {
      if (!this.searchKeyword) {
        return this.tasks
      }
      const keyword = this.searchKeyword.toLowerCase()
      return this.tasks.filter(task => {
        return (
          (task.task_name && task.task_name.toLowerCase().includes(keyword)) ||
          (task.api_endpoint && task.api_endpoint.toLowerCase().includes(keyword))
        )
      })
    }
  },
  mounted() {
    this.loadTasks()
  },
  methods: {
    handleSearch() {
      // 搜索逻辑由 computed filteredTasks 自动处理
    },

    async loadTasks() {
      this.loading = true
      try {
        const response = await axios.get('/scheduled-tasks/tasks')
        if (response.data.status === 'success') {
          this.tasks = response.data.data
        }
      } catch (error) {
        this.$message.error('加载任务列表失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    showCreateDialog() {
      this.dialogMode = 'create'
      this.taskForm = {
        task_name: '',
        api_endpoint: '/stock/bill/history/fetch',
        http_method: 'POST',
        parameters: {},
        schedule_type: 'cron',
        schedule_config: {
          hour: 16,
          minute: 0,
          second: 0
        },
        enabled: true
      }
      this.parametersJson = '{"stock_code": "000001"}'
      this.intervalValue = 10
      this.intervalUnit = 'seconds'
      this.dialogVisible = true
    },

    editTask(task) {
      this.dialogMode = 'edit'
      this.currentTask = task
      this.taskForm = {
        task_name: task.task_name,
        api_endpoint: task.api_endpoint,
        http_method: task.http_method,
        parameters: task.parameters || {},
        schedule_type: task.schedule_type,
        schedule_config: task.schedule_config || {},
        enabled: task.enabled
      }
      this.parametersJson = JSON.stringify(task.parameters || {}, null, 2)

      if (task.schedule_type === 'interval') {
        const config = task.schedule_config
        if (config.seconds) {
          this.intervalValue = config.seconds
          this.intervalUnit = 'seconds'
        } else if (config.minutes) {
          this.intervalValue = config.minutes
          this.intervalUnit = 'minutes'
        } else if (config.hours) {
          this.intervalValue = config.hours
          this.intervalUnit = 'hours'
        } else if (config.days) {
          this.intervalValue = config.days
          this.intervalUnit = 'days'
        }
      }

      this.dialogVisible = true
    },

    onScheduleTypeChange(type) {
      if (type === 'cron') {
        this.taskForm.schedule_config = {
          hour: 16,
          minute: 0,
          second: 0
        }
      } else {
        this.taskForm.schedule_config = {
          seconds: 10
        }
        this.intervalValue = 10
        this.intervalUnit = 'seconds'
      }
    },

    async saveTask() {
      this.$refs.taskForm.validate(async (valid) => {
        if (!valid) return

        // 解析参数JSON
        try {
          if (this.parametersJson.trim()) {
            this.taskForm.parameters = JSON.parse(this.parametersJson)
          } else {
            this.taskForm.parameters = {}
          }
        } catch (e) {
          this.$message.error('请求参数JSON格式错误')
          return
        }

        // 更新间隔配置
        if (this.taskForm.schedule_type === 'interval') {
          this.taskForm.schedule_config = {}
          this.taskForm.schedule_config[this.intervalUnit] = this.intervalValue
        }

        this.saving = true
        try {
          if (this.dialogMode === 'create') {
            await axios.post('/scheduled-tasks/tasks', this.taskForm)
            this.$message.success('任务创建成功')
          } else {
            await axios.put(`/scheduled-tasks/tasks/${this.currentTask.id}`, this.taskForm)
            this.$message.success('任务更新成功')
          }
          this.dialogVisible = false
          this.loadTasks()
        } catch (error) {
          this.$message.error('保存失败: ' + (error.response?.data?.error || error.message))
        } finally {
          this.saving = false
        }
      })
    },

    async toggleTask(task) {
      try {
        await axios.post(`/scheduled-tasks/tasks/${task.id}/toggle`)
        this.$message.success(task.enabled ? '任务已启用' : '任务已禁用')
        this.loadTasks()
      } catch (error) {
        this.$message.error('操作失败: ' + error.message)
        task.enabled = !task.enabled
      }
    },

    async executeNow(task) {
      this.$confirm('确认立即执行此任务？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await axios.post(`/scheduled-tasks/tasks/${task.id}/execute`)
          this.$message.success('任务已开始执行')
        } catch (error) {
          this.$message.error('执行失败: ' + error.message)
        }
      }).catch(() => {})
    },

    async deleteTask(task) {
      this.$confirm('确认删除此任务？删除后不可恢复！', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await axios.delete(`/scheduled-tasks/tasks/${task.id}`)
          this.$message.success('任务已删除')
          this.loadTasks()
        } catch (error) {
          this.$message.error('删除失败: ' + error.message)
        }
      }).catch(() => {})
    },

    async showHistory(task) {
      this.currentTask = task
      this.historyDialogVisible = true
      this.loadingHistory = true
      try {
        const response = await axios.get(`/scheduled-tasks/tasks/${task.id}/executions?limit=20`)
        if (response.data.status === 'success') {
          this.executions = response.data.data
        }
      } catch (error) {
        this.$message.error('加载执行历史失败: ' + error.message)
      } finally {
        this.loadingHistory = false
      }
    },

    formatScheduleConfig(task) {
      if (task.schedule_type === 'cron') {
        const config = task.schedule_config
        return `${config.hour || '*'}:${config.minute || '*'} 每天`
      } else {
        const config = task.schedule_config
        if (config.seconds) return `每${config.seconds}秒`
        if (config.minutes) return `每${config.minutes}分钟`
        if (config.hours) return `每${config.hours}小时`
        if (config.days) return `每${config.days}天`
        return '-'
      }
    },

    statusText(status) {
      const map = {
        running: '运行中',
        success: '成功',
        failed: '失败'
      }
      return map[status] || status
    }
  }
}
</script>

<style scoped>
.scheduled-tasks {
  padding: 20px;
  width: 100%;
  background: transparent;
  color: #e2e8f0;
}

/* 全局覆盖 - 确保dialog内的输入框可见 */
:deep(.el-dialog__body) {
  color: #cbd5e1;
}

:deep(.el-dialog__body .el-input__inner),
:deep(.el-dialog__body .el-textarea__inner) {
  background: rgba(30, 41, 59, 0.8) !important;
  border-color: rgba(99, 102, 241, 0.3) !important;
  color: #e2e8f0 !important;
}

:deep(.el-dialog__body .el-input__inner:focus),
:deep(.el-dialog__body .el-textarea__inner:focus) {
  border-color: rgba(99, 102, 241, 0.6) !important;
}

:deep(.el-dialog__body .el-input-number .el-input__inner) {
  background: rgba(30, 41, 59, 0.8) !important;
  border-color: rgba(99, 102, 241, 0.3) !important;
  color: #e2e8f0 !important;
}

/* 确保对话框遮罩层和内容可交互 */
:deep(.v-modal) {
  z-index: 2000 !important;
}

:deep(.el-dialog__wrapper) {
  z-index: 2001 !important;
}

:deep(.el-dialog) {
  z-index: 2002 !important;
}

:deep(.el-dialog__headerbtn) {
  z-index: 2003 !important;
  pointer-events: auto !important;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  color: #e2e8f0;
  font-weight: 600;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.filter-bar .search-input {
  width: 300px;
}

.filter-bar :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: #e2e8f0;
  border-radius: 6px;
}

.filter-bar :deep(.el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.5);
}

.filter-bar :deep(.el-input__inner):focus {
  border-color: #6366f1;
}

.filter-bar :deep(.input-icon) {
  color: rgba(99, 102, 241, 0.6);
}

.filter-bar :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
}

.filter-bar :deep(.el-button--default) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: #e2e8f0;
  border-radius: 6px;
  padding: 10px 20px;
}

.filter-bar :deep(.el-button--default):hover {
  border-color: rgba(99, 102, 241, 0.5);
  background: rgba(99, 102, 241, 0.1);
}

.tasks-table {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.tasks-table :deep(.el-table) {
  background: transparent;
}

.tasks-table :deep(.el-table__body tr:hover > td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

.delete-btn {
  color: #ef4444;
}

.delete-btn:hover {
  color: #f87171;
}

.form-tip {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

.cron-config {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cron-config span {
  color: #cbd5e1;
}

.interval-config {
  display: flex;
  align-items: center;
  gap: 8px;
}

.result-cell {
  font-size: 13px;
  word-break: break-all;
}

.result-cell.error {
  color: #ef4444;
}

/* 对话框样式 */
:deep(.task-dialog),
:deep(.history-dialog) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(99, 102, 241, 0.3) !important;
  border-radius: 12px !important;
  pointer-events: auto !important;
}

:deep(.task-dialog .el-dialog__header),
:deep(.history-dialog .el-dialog__header) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  padding: 16px 20px;
}

:deep(.task-dialog .el-dialog__title),
:deep(.history-dialog .el-dialog__title) {
  color: #e2e8f0;
}

:deep(.task-dialog .el-dialog__body),
:deep(.history-dialog .el-dialog__body) {
  padding: 20px;
  color: #cbd5e1;
  pointer-events: auto !important;
}

:deep(.task-dialog .el-dialog__footer),
:deep(.history-dialog .el-dialog__footer) {
  border-top: 1px solid rgba(99, 102, 241, 0.2);
  padding: 12px 20px;
  pointer-events: auto !important;
}

:deep(.task-dialog .el-form-item__label),
:deep(.history-dialog .el-form-item__label) {
  color: #cbd5e1;
}

/* 确保所有表单元素可点击 */
:deep(.task-dialog .el-input),
:deep(.task-dialog .el-select),
:deep(.task-dialog .el-input-number),
:deep(.task-dialog .el-textarea),
:deep(.task-dialog .el-radio-group),
:deep(.task-dialog .el-switch),
:deep(.task-dialog .el-button) {
  pointer-events: auto !important;
}

:deep(.task-dialog .el-input__inner),
:deep(.task-dialog .el-textarea__inner) {
  pointer-events: auto !important;
}

:deep(.task-dialog .el-input__inner),
:deep(.history-dialog .el-input__inner),
:deep(.task-dialog .el-textarea__inner),
:deep(.history-dialog .el-textarea__inner) {
  background: rgba(30, 41, 59, 0.8) !important;
  border-color: rgba(99, 102, 241, 0.3) !important;
  color: #e2e8f0 !important;
}

:deep(.task-dialog .el-input__inner:focus),
:deep(.history-dialog .el-input__inner:focus),
:deep(.task-dialog .el-textarea__inner:focus),
:deep(.history-dialog .el-textarea__inner:focus) {
  border-color: rgba(99, 102, 241, 0.6) !important;
}

:deep(.task-dialog .el-input__inner::placeholder),
:deep(.history-dialog .el-input__inner::placeholder),
:deep(.task-dialog .el-textarea__inner::placeholder),
:deep(.history-dialog .el-textarea__inner::placeholder) {
  color: #94a3b8 !important;
}

:deep(.task-dialog .el-input-number .el-input__inner) {
  background: rgba(30, 41, 59, 0.8) !important;
  border-color: rgba(99, 102, 241, 0.3) !important;
  color: #e2e8f0 !important;
}

:deep(.task-dialog .el-input-number__increase),
:deep(.task-dialog .el-input-number__decrease) {
  background: rgba(30, 41, 59, 0.8) !important;
  border-color: rgba(99, 102, 241, 0.3) !important;
  color: #94a3b8 !important;
}

:deep(.task-dialog .el-input-number__increase:hover),
:deep(.task-dialog .el-input-number__decrease:hover) {
  color: #a5b4fc !important;
}

:deep(.task-dialog .el-select-dropdown) {
  background: rgba(15, 23, 42, 0.95);
  border-color: rgba(99, 102, 241, 0.3);
}

:deep(.task-dialog .el-select-dropdown__item) {
  color: #cbd5e1;
}

:deep(.task-dialog .el-select-dropdown__item.hover),
:deep(.task-dialog .el-select-dropdown__item:hover) {
  background: rgba(99, 102, 241, 0.2);
}

:deep(.task-dialog .el-radio__label) {
  color: #cbd5e1;
}

:deep(.task-dialog .el-radio__input.is-checked .el-radio__inner) {
  background: #6366f1;
  border-color: #6366f1;
}
</style>
